import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { adminGet, adminPost, adminPut, adminDel } from "@/lib/adminApi";
import { capFirst } from "@/lib/profile";

const EMPTY_TARIFF = { label: "", price: 0, period: "", valid_from: "", valid_until: "", status: "actif" };

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => adminGet("/offers").then((d) => setOffers(d.offers)).catch(() => setOffers([]));
  useEffect(() => {
    load();
  }, []);

  const tariffs = form?.tariffs || [];
  const setTariff = (i, patch) => setForm({ ...form, tariffs: tariffs.map((t, j) => (j === i ? { ...t, ...patch } : t)) });

  const save = async () => {
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      credits: Number(form.credits) || 0,
      tariffs: tariffs.map((t) => ({ ...t, price: Number(t.price) || 0 })),
    };
    try {
      if (form.id) await adminPut(`/offers/${form.id}`, payload);
      else await adminPost("/offers", payload);
      toast("Offre enregistrée.");
      setForm(null);
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const remove = async (o) => {
    await adminDel(`/offers/${o.id}`).catch(() => {});
    toast("Offre supprimée.");
    load();
  };

  return (
    <div data-testid="admin-offers-module">
      <h2 className="rav-admin-h2">Abonnements & offres — tarifs modulables</h2>
      <button type="button" className="rav-primary-button rav-admin-create" data-testid="offers-create" onClick={() => setForm({ tariffs: [] })}>
        + Ajouter une offre
      </button>

      {form && (
        <div className="rav-admin-form" data-testid="offers-form">
          <input className="rav-input" data-testid="offers-form-name" placeholder="Nom de l'offre" value={form.name || ""} onChange={(e) => setForm({ ...form, name: capFirst(e.target.value) })} />
          <input className="rav-input" data-testid="offers-form-kind" placeholder="Type (abonnement, programme, visio, cadeau)" value={form.kind || ""} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
          <input className="rav-input" data-testid="offers-form-price" type="number" step="0.01" placeholder="Prix (€)" value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <input className="rav-input" data-testid="offers-form-period" placeholder="Période (mensuel, annuel, séance…)" value={form.period || ""} onChange={(e) => setForm({ ...form, period: e.target.value })} />
          <input className="rav-input" data-testid="offers-form-description" placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: capFirst(e.target.value) })} />
          <input className="rav-input" data-testid="offers-form-status" placeholder="Statut (actif / inactif)" value={form.status || ""} onChange={(e) => setForm({ ...form, status: e.target.value })} />
          <input className="rav-input" data-testid="offers-form-credits" type="number" min="0" placeholder="Instants crédités (0 = illimité)" value={form.credits ?? ""} onChange={(e) => setForm({ ...form, credits: e.target.value })} />

          <h3 className="rav-admin-h3">Tarifs spécifiques (lancement, promo, période…)</h3>
          {tariffs.map((t, i) => (
            <div key={i} className="rav-admin-tariff" data-testid={`tariff-row-${i}`}>
              <input className="rav-input" data-testid={`tariff-label-${i}`} placeholder="Libellé (ex. Tarif de lancement)" value={t.label} onChange={(e) => setTariff(i, { label: e.target.value })} />
              <input className="rav-input" data-testid={`tariff-price-${i}`} type="number" step="0.01" placeholder="Prix (€)" value={t.price} onChange={(e) => setTariff(i, { price: e.target.value })} />
              <input className="rav-input" data-testid={`tariff-period-${i}`} placeholder="Période (ex. première année)" value={t.period} onChange={(e) => setTariff(i, { period: e.target.value })} />
              <label className="rav-field"><span>Valable du</span><input className="rav-input" data-testid={`tariff-from-${i}`} type="date" value={t.valid_from} onChange={(e) => setTariff(i, { valid_from: e.target.value })} /></label>
              <label className="rav-field"><span>au</span><input className="rav-input" data-testid={`tariff-until-${i}`} type="date" value={t.valid_until} onChange={(e) => setTariff(i, { valid_until: e.target.value })} /></label>
              <button type="button" className="rav-text-link" data-testid={`tariff-remove-${i}`} onClick={() => setForm({ ...form, tariffs: tariffs.filter((_, j) => j !== i) })}>
                <Trash2 size={14} strokeWidth={1.6} /> Retirer
              </button>
            </div>
          ))}
          <button type="button" className="rav-secondary-button rav-session-btn" data-testid="tariff-add" onClick={() => setForm({ ...form, tariffs: [...tariffs, { ...EMPTY_TARIFF }] })}>
            <Plus size={14} strokeWidth={1.6} /> Ajouter un tarif spécifique
          </button>

          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="offers-form-save" onClick={save}>
              Enregistrer
            </button>
            <button type="button" className="rav-text-link" data-testid="offers-form-cancel" onClick={() => setForm(null)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="rav-admin-list">
        {offers.map((o, i) => (
          <div key={o.id} className="rav-admin-row" data-testid={`offers-row-${i}`}>
            <span className="rav-admin-row-label">{o.name}</span>
            <span className="rav-admin-row-sub">
              {o.price} € · {o.period} · {o.status}
              {(o.tariffs || []).length > 0 ? ` · ${o.tariffs.length} tarif(s) spécifique(s)` : ""}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`offers-edit-${i}`} onClick={() => setForm({ ...o, tariffs: o.tariffs || [] })}>
                Modifier
              </button>
              <button type="button" className="rav-text-link" data-testid={`offers-delete-${i}`} onClick={() => remove(o)}>
                Supprimer
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
